import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { CambiTurnoPanel } from "./RichiesteTurni";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarDays, Plus, Check, X, Clock, Sun, Thermometer, BedDouble,
  ChevronDown, Filter, Settings, Save, Upload, FileText, ShieldOff, AlertCircle, Trash2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPI_ASSENZA = [
  { value: "ferie", label: "Ferie", icon: Sun, color: "bg-amber-500" },
  { value: "permesso", label: "Permesso", icon: Clock, color: "bg-blue-500" },
  { value: "malattia", label: "Malattia", icon: Thermometer, color: "bg-red-500" },
  { value: "riposo", label: "Riposo", icon: BedDouble, color: "bg-purple-500" }
];

const FeriePermessi = () => {
  const { getAuthHeaders, localeCorrente, user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  
  // Dati
  const [richieste, setRichieste] = useState([]);
  const [saldoFerie, setSaldoFerie] = useState(null);
  
  // Dialog
  const [dialogNuova, setDialogNuova] = useState(false);
  const [dialogDettaglio, setDialogDettaglio] = useState(false);
  const [dialogImpostazioni, setDialogImpostazioni] = useState(false);
  const [richiestaSelezionata, setRichiestaSelezionata] = useState(null);
  const [preavvisoGiorni, setPreavvisoGiorni] = useState(7);
  const [savingImpostazioni, setSavingImpostazioni] = useState(false);
  const [form, setForm] = useState({
    tipo: "ferie",
    data_inizio: "",
    data_fine: "",
    ora_inizio: "",
    ora_fine: "",
    motivazione: ""
  });
  const [rispostaNota, setRispostaNota] = useState("");

  useEffect(() => {
    if (localeCorrente?.id) {
      fetchData();
    }
  }, [localeCorrente?.id, activeTab]);

  const fetchData = async () => {
    if (!localeCorrente?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ locale_id: localeCorrente.id });
      
      // Se non admin, filtra solo le proprie richieste
      if (!isAdmin) {
        params.append("utente_id", user?.id);
      }
      
      if (activeTab !== "all") {
        params.append("stato", activeTab);
      }
      
      const requests = [
        axios.get(`${API}/personale/assenze?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/personale/saldo-ferie/${user?.id}`, { headers: getAuthHeaders() })
      ];
      
      // Se admin, carica anche le impostazioni
      if (isAdmin) {
        requests.push(axios.get(`${API}/personale/impostazioni?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }));
      }
      
      const responses = await Promise.all(requests);
      
      setRichieste(responses[0].data);
      setSaldoFerie(responses[1].data);
      
      if (isAdmin && responses[2]) {
        setPreavvisoGiorni(responses[2].data?.preavviso_ferie_giorni || 7);
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const openImpostazioni = () => {
    setDialogImpostazioni(true);
  };

  const handleSalvaImpostazioni = async () => {
    setSavingImpostazioni(true);
    try {
      await axios.post(
        `${API}/personale/impostazioni?locale_id=${localeCorrente.id}`,
        { preavviso_ferie_giorni: preavvisoGiorni },
        { headers: getAuthHeaders() }
      );
      toast.success("Impostazioni salvate");
      setDialogImpostazioni(false);
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingImpostazioni(false);
    }
  };

  const handleNuovaRichiesta = async () => {
    if (!form.data_inizio || !form.data_fine) {
      toast.error("Inserisci le date");
      return;
    }
    
    try {
      await axios.post(
        `${API}/personale/assenze?locale_id=${localeCorrente.id}&utente_id=${user?.id}`,
        form,
        { headers: getAuthHeaders() }
      );
      toast.success(form.tipo === "malattia" ? "Malattia registrata" : "Richiesta inviata");
      setDialogNuova(false);
      setForm({ tipo: "ferie", data_inizio: "", data_fine: "", ora_inizio: "", ora_fine: "", motivazione: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio");
    }
  };

  const handleRispondi = async (stato) => {
    try {
      await axios.put(
        `${API}/personale/assenze/${richiestaSelezionata.id}/risposta?admin_id=${user?.id}`,
        { stato, note: rispostaNota },
        { headers: getAuthHeaders() }
      );
      toast.success(stato === "approvata" ? "Richiesta approvata" : "Richiesta rifiutata");
      setDialogDettaglio(false);
      setRichiestaSelezionata(null);
      setRispostaNota("");
      fetchData();
    } catch (error) {
      toast.error("Errore nella risposta");
    }
  };

  const handleUploadCertificato = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !richiestaSelezionata) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(
        `${API}/personale/assenze/${richiestaSelezionata.id}/certificato`,
        formData,
        { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } }
      );
      toast.success("Certificato caricato");
      fetchData();
      setRichiestaSelezionata(prev => ({ ...prev, certificato_medico_url: "uploaded", certificato_richiesto: false }));
    } catch (error) {
      toast.error("Errore nel caricamento");
    }
  };

  const handleRinunciaCertificato = async () => {
    if (!richiestaSelezionata) return;
    try {
      await axios.put(
        `${API}/personale/assenze/${richiestaSelezionata.id}/rinuncia-certificato`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Certificato non più richiesto");
      fetchData();
      setRichiestaSelezionata(prev => ({ ...prev, certificato_richiesto: false, certificato_non_richiesto: true }));
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleElimina = async () => {
    if (!richiestaSelezionata) return;
    try {
      await axios.delete(
        `${API}/personale/assenze/${richiestaSelezionata.id}`,
        { headers: getAuthHeaders() }
      );
      toast.success("Richiesta eliminata");
      setDialogDettaglio(false);
      setRichiestaSelezionata(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const openDettaglio = (richiesta) => {
    setRichiestaSelezionata(richiesta);
    setRispostaNota("");
    setDialogDettaglio(true);
  };

  const getTipoInfo = (tipo) => TIPI_ASSENZA.find(t => t.value === tipo) || TIPI_ASSENZA[0];

  const getStatoBadge = (stato) => {
    switch (stato) {
      case "pending":
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">In attesa</span>;
      case "approvata":
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Approvata</span>;
      case "rifiutata":
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Rifiutata</span>;
      case "presa_visione":
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Presa visione</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return <div className="loading-state"><div className="text-muted-custom">Caricamento...</div></div>;
  }

  return (
    <div className="page-container" data-testid="ferie-permessi-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-primary" />
            Ferie & Permessi
          </h1>
          <p className="page-subtitle">
            {isAdmin ? "Gestisci le richieste del personale" : "Richiedi ferie, permessi o malattia"}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button 
              onClick={openImpostazioni} 
              variant="outline" 
              className="btn-secondary gap-2"
            >
              <Settings className="h-4 w-4" /> Imposta giorni di preavviso
            </Button>
          )}
          {!isAdmin && (
            <Button onClick={() => setDialogNuova(true)} className="btn-primary gap-2">
              <Plus className="h-4 w-4" /> Nuova Richiesta
            </Button>
          )}
        </div>
      </div>

      {/* Saldo Ferie Card - solo per operatori */}
      {!isAdmin && saldoFerie && (
        <div className="bg-gradient-to-r from-primary/10 to-blue-50 rounded-2xl p-5 border border-primary/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-foreground text-sm">Ferie Annuali Rimanenti {saldoFerie.anno}</p>
              <p className="text-3xl font-bold text-primary mt-1">
                {saldoFerie.giorni_residui}<span className="text-xl text-muted-foreground font-normal">/{saldoFerie.giorni_totali}</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Giorni usati: {saldoFerie.giorni_usati}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="tabs-pill-list">
          <TabsTrigger value="pending">In Attesa</TabsTrigger>
          <TabsTrigger value="approvata">Approvate</TabsTrigger>
          <TabsTrigger value="rifiutata">Rifiutate</TabsTrigger>
          <TabsTrigger value="presa_visione">Malattia</TabsTrigger>
          <TabsTrigger value="all">Tutte</TabsTrigger>
          <TabsTrigger value="cambi_turno">Cambi Turno</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Cambi Turno - componente separato */}
      {activeTab === "cambi_turno" ? (
        <CambiTurnoPanel />
      ) : (
      <>
      {/* Lista Richieste */}
      {richieste.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-2xl border border-border">
          <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium text-foreground">Nessuna richiesta</h3>
          <p className="text-muted-foreground text-sm mt-1">
            {activeTab === "pending" ? "Non ci sono richieste in attesa" : "Non ci sono richieste in questa categoria"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {richieste.map(richiesta => {
            const tipoInfo = getTipoInfo(richiesta.tipo);
            const Icon = tipoInfo.icon;
            
            return (
              <button
                key={richiesta.id}
                onClick={() => openDettaglio(richiesta)}
                className="w-full p-4 bg-card rounded-2xl border border-border hover:border-primary/30 transition-colors text-left shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl ${tipoInfo.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-medium text-foreground">{tipoInfo.label}</h3>
                      {getStatoBadge(richiesta.stato)}
                    </div>
                    {isAdmin && (
                      <p className="text-sm text-primary font-medium mt-0.5">{richiesta.utente_nome}</p>
                    )}
                    <p className="text-sm text-muted-foreground mt-1">
                      {new Date(richiesta.data_inizio).toLocaleDateString("it-IT")}
                      {richiesta.data_inizio !== richiesta.data_fine && (
                        <> - {new Date(richiesta.data_fine).toLocaleDateString("it-IT")}</>
                      )}
                    </p>
                    {richiesta.motivazione && (
                      <p className="text-sm text-muted-foreground mt-1 truncate">{richiesta.motivazione}</p>
                    )}
                    {/* Reminder certificato medico per operatore */}
                    {!isAdmin && richiesta.tipo === "malattia" && richiesta.certificato_richiesto && !richiesta.certificato_medico_url && (
                      <div className="flex items-center gap-1.5 mt-2 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-lg w-fit" data-testid="cert-reminder">
                        <AlertCircle className="h-3.5 w-3.5" />
                        Carica il certificato medico
                      </div>
                    )}
                    {/* Certificato caricato */}
                    {richiesta.tipo === "malattia" && richiesta.certificato_medico_url && (
                      <div className="flex items-center gap-1.5 mt-2 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-lg w-fit">
                        <FileText className="h-3.5 w-3.5" />
                        Certificato caricato
                      </div>
                    )}
                    {/* Admin: certificato in attesa */}
                    {isAdmin && richiesta.tipo === "malattia" && richiesta.certificato_richiesto && !richiesta.certificato_medico_url && !richiesta.certificato_non_richiesto && (
                      <div className="flex items-center gap-1.5 mt-2 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-lg w-fit">
                        <AlertCircle className="h-3.5 w-3.5" />
                        In attesa del certificato
                      </div>
                    )}
                    {/* Admin: certificato non richiesto */}
                    {richiesta.tipo === "malattia" && richiesta.certificato_non_richiesto && (
                      <div className="flex items-center gap-1.5 mt-2 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-lg w-fit">
                        <ShieldOff className="h-3.5 w-3.5" />
                        Certificato non richiesto
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Dialog Nuova Richiesta */}
      </>
      )}

      {/* Dialog Nuova Richiesta */}
      <Dialog open={dialogNuova} onOpenChange={setDialogNuova}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Nuova Richiesta</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-muted-foreground">Tipo *</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger className="bg-muted border-border text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPI_ASSENZA.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.tipo === "malattia" && (
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 text-sm text-blue-700">
                <p className="font-medium flex items-center gap-1.5"><BedDouble className="h-4 w-4" /> Informazioni sulla malattia</p>
                <ul className="mt-1.5 space-y-1 text-xs text-blue-600">
                  <li>- Non è richiesto preavviso</li>
                  <li>- La richiesta non necessita di approvazione</li>
                  <li>- Ti verrà chiesto di caricare il certificato medico</li>
                </ul>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Data Inizio *</Label>
                <DateInput value={form.data_inizio} onChange={(v) => setForm({ ...form, data_inizio: v, data_fine: form.data_fine || v })} />
              </div>
              <div>
                <Label className="text-muted-foreground">Data Fine *</Label>
                <DateInput value={form.data_fine} onChange={(v) => setForm({ ...form, data_fine: v })} />
              </div>
            </div>
            {form.tipo === "permesso" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Ora Inizio</Label>
                  <Input
                    type="time"
                    value={form.ora_inizio}
                    onChange={(e) => setForm({ ...form, ora_inizio: e.target.value })}
                    className="bg-muted border-border text-foreground"
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground">Ora Fine</Label>
                  <Input
                    type="time"
                    value={form.ora_fine}
                    onChange={(e) => setForm({ ...form, ora_fine: e.target.value })}
                    className="bg-muted border-border text-foreground"
                  />
                </div>
              </div>
            )}
            <div>
              <Label className="text-muted-foreground">Motivazione</Label>
              <Textarea
                value={form.motivazione}
                onChange={(e) => setForm({ ...form, motivazione: e.target.value })}
                placeholder="Motivo della richiesta (opzionale)"
                className="bg-muted border-border text-foreground"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 pt-4">
            <Button variant="outline" onClick={() => setDialogNuova(false)}>Annulla</Button>
            <Button onClick={handleNuovaRichiesta} className="bg-primary text-white">Invia Richiesta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog dettaglio richiesta */}
      <Dialog open={dialogDettaglio} onOpenChange={setDialogDettaglio}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Dettaglio Richiesta</DialogTitle>
          </DialogHeader>
          {richiestaSelezionata && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                {(() => {
                  const tipoInfo = getTipoInfo(richiestaSelezionata.tipo);
                  const Icon = tipoInfo.icon;
                  return (
                    <>
                      <div className={`w-12 h-12 rounded-xl ${tipoInfo.color} flex items-center justify-center`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground">{tipoInfo.label}</h3>
                        {getStatoBadge(richiestaSelezionata.stato)}
                      </div>
                    </>
                  );
                })()}
              </div>
              
              <div className="p-3 bg-muted rounded-xl space-y-2">
                <p className="text-sm"><span className="text-muted-foreground">Richiedente:</span> <span className="text-foreground font-medium">{richiestaSelezionata.utente_nome}</span></p>
                <p className="text-sm"><span className="text-muted-foreground">Periodo:</span> <span className="text-foreground font-medium">
                  {new Date(richiestaSelezionata.data_inizio).toLocaleDateString("it-IT")}
                  {richiestaSelezionata.data_inizio !== richiestaSelezionata.data_fine && (
                    <> - {new Date(richiestaSelezionata.data_fine).toLocaleDateString("it-IT")}</>
                  )}
                </span></p>
                {richiestaSelezionata.motivazione && (
                  <p className="text-sm"><span className="text-muted-foreground">Motivazione:</span> <span className="text-foreground">{richiestaSelezionata.motivazione}</span></p>
                )}
                {richiestaSelezionata.risposta_note && (
                  <p className="text-sm"><span className="text-muted-foreground">Note risposta:</span> <span className="text-foreground">{richiestaSelezionata.risposta_note}</span></p>
                )}
              </div>

              {/* === SEZIONE CERTIFICATO MEDICO (solo malattia) === */}
              {richiestaSelezionata.tipo === "malattia" && (
                <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 space-y-3">
                  <p className="text-sm font-semibold text-blue-800 flex items-center gap-1.5">
                    <FileText className="h-4 w-4" /> Certificato Medico
                  </p>

                  {/* Certificato caricato */}
                  {richiestaSelezionata.certificato_medico_url && (
                    <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-2 rounded-lg">
                      <Check className="h-4 w-4" />
                      <span className="font-medium">Certificato caricato</span>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${richiestaSelezionata.certificato_medico_url}`} target="_blank" rel="noopener noreferrer"
                        className="ml-auto text-xs underline text-green-600">Scarica</a>
                    </div>
                  )}

                  {/* Certificato non richiesto dall'admin */}
                  {richiestaSelezionata.certificato_non_richiesto && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-100 p-2 rounded-lg">
                      <ShieldOff className="h-4 w-4" />
                      <span className="font-medium">L'admin non richiede il certificato</span>
                    </div>
                  )}

                  {/* Operatore: pulsante upload (se serve ancora) */}
                  {!isAdmin && richiestaSelezionata.certificato_richiesto && !richiestaSelezionata.certificato_medico_url && !richiestaSelezionata.certificato_non_richiesto && (
                    <div>
                      <p className="text-xs text-amber-700 mb-2 flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5" />
                        Ricordati di caricare il certificato medico
                      </p>
                      <label className="flex items-center gap-2 cursor-pointer px-4 py-2.5 bg-white border border-blue-200 rounded-xl text-sm font-medium text-blue-700 hover:bg-blue-50 transition-colors w-fit">
                        <Upload className="h-4 w-4" />
                        Carica certificato
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleUploadCertificato} data-testid="upload-cert-input" />
                      </label>
                    </div>
                  )}

                  {/* Admin: pulsante rinuncia certificato */}
                  {isAdmin && richiestaSelezionata.certificato_richiesto && !richiestaSelezionata.certificato_medico_url && !richiestaSelezionata.certificato_non_richiesto && (
                    <div>
                      <p className="text-xs text-amber-700 mb-2">In attesa del certificato dal dipendente</p>
                      <Button onClick={handleRinunciaCertificato} variant="outline" className="text-sm gap-2 border-gray-300" data-testid="rinuncia-cert-btn">
                        <ShieldOff className="h-4 w-4" /> Non richiedere certificato
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* === AZIONI ADMIN: Approva/Rifiuta (solo ferie e permessi in pending) === */}
              {isAdmin && richiestaSelezionata.stato === "pending" && richiestaSelezionata.tipo !== "malattia" && (
                <>
                  <div>
                    <Label className="text-muted-foreground">Note (opzionale)</Label>
                    <Textarea
                      value={rispostaNota}
                      onChange={(e) => setRispostaNota(e.target.value)}
                      placeholder="Aggiungi una nota alla risposta"
                      className="bg-muted border-border text-foreground"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleRispondi("rifiutata")}
                      variant="outline"
                      className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <X className="h-4 w-4 mr-2" /> Rifiuta
                    </Button>
                    <Button
                      onClick={() => handleRispondi("approvata")}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                    >
                      <Check className="h-4 w-4 mr-2" /> Approva
                    </Button>
                  </div>
                </>
              )}

              {/* Bottone elimina — operatore elimina la propria richiesta */}
              {!isAdmin && (richiestaSelezionata.stato === "pending" || richiestaSelezionata.stato === "presa_visione") && (
                <Button onClick={handleElimina} variant="outline" className="w-full text-red-500 border-red-200 hover:bg-red-50 gap-2" data-testid="delete-request-btn">
                  <Trash2 className="h-4 w-4" /> Elimina richiesta
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Impostazioni Preavviso */}
      <Dialog open={dialogImpostazioni} onOpenChange={setDialogImpostazioni}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              Impostazioni Preavviso Ferie
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-foreground">Giorni di preavviso minimi</Label>
              <Input
                type="number"
                min="0"
                max="90"
                value={preavvisoGiorni}
                onChange={(e) => setPreavvisoGiorni(parseInt(e.target.value) || 0)}
                className="bg-muted border-border text-foreground rounded-xl"
              />
              <p className="text-sm text-muted-foreground">
                Gli operatori dovranno richiedere le ferie con almeno <strong>{preavvisoGiorni}</strong> giorni di anticipo
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDialogImpostazioni(false)}
              className="border-border text-foreground hover:bg-muted"
            >
              Annulla
            </Button>
            <Button 
              onClick={handleSalvaImpostazioni}
              disabled={savingImpostazioni}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingImpostazioni ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeriePermessi;
